import { useFormContext, Controller } from "react-hook-form";
import TextField from "@/components/common/text-field";
import type { NotificationNodeData } from "@/schemas/workflow/notification-node";
import { RecipientInput } from "./recipient-input";
import { useGetVariables } from "@/workflow_api/hooks/variables/useGetVariables";
import BasicRichTextEditor from "@/components/common/editor/basic-rich-text-editor";
import { AttachmentInput } from "./attachment-input";
import { useGetEmailTemplateById } from "@/queries/email-templates/email-templates.api";
import { useEffect } from "react";
import { TemplateVariableInput } from "./template-variable-input";
import { TemplateSelector } from "./template-selector";

export const NotificationForm = () => {
  const {
    register,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useFormContext<NotificationNodeData>();

  const { variables } = useGetVariables();

  console.log(variables);

  const template = watch("template") || "no-template";

  // Fetch template details when a template is selected (but not for "no-template")
  const { data: templateDetails } = useGetEmailTemplateById(
    template === "no-template" ? "" : template,
  );
  const selectedTemplate = templateDetails?.data;

  // Usar variables reales (incluye DOC_GENERATOR desde useGetVariables)
  const allVariables = variables;

  const title = watch("title") || "";
  const attachments = watch("attachments") || [];
  const emails = watch("emails") || [];
  const templateVariables = watch("variables") || {};

  const isNoTemplate = template === "no-template";

  // Initialize template variables when a template is selected
  useEffect(() => {
    if (selectedTemplate && selectedTemplate.required_variables) {
      setValue("required_variables", selectedTemplate.required_variables, {
        shouldValidate: true,
      });

      const newVariables: {
        [key: string]: {
          type: "inp" | "var";
          value: string;
          var_type: "STRING" | "DATE" | "FLOAT";
        }[];
      } = {
        ...templateVariables,
      };

      selectedTemplate.required_variables.forEach((varName) => {
        if (!newVariables[varName] || newVariables[varName].length === 0) {
          const config = selectedTemplate.variables[varName];
          newVariables[varName] = [
            {
              type: "inp",
              value: "",
              var_type: config.type.toUpperCase() as
                | "STRING"
                | "DATE"
                | "FLOAT",
            },
          ];
        }
      });

      if (selectedTemplate.optional_variables) {
        selectedTemplate.optional_variables.forEach((varName) => {
          if (!newVariables[varName] || newVariables[varName].length === 0) {
            const config = selectedTemplate.variables[varName];
            newVariables[varName] = [
              {
                type: "inp",
                value: "",
                var_type: config.type.toUpperCase() as
                  | "STRING"
                  | "DATE"
                  | "FLOAT",
              },
            ];
          }
        });
      }

      // remove variables that are not in required or optional lists
      Object.keys(newVariables).forEach((varName) => {
        if (
          !selectedTemplate.required_variables?.includes(varName) &&
          !selectedTemplate.optional_variables?.includes(varName)
        ) {
          delete newVariables[varName];
        }
      });

      setValue("variables", newVariables, { shouldValidate: true });
    } else if (template === "no-template") {
      setValue("variables", {}, { shouldValidate: false });
      setValue("required_variables", [], { shouldValidate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, template]);
  return (
    <div className="py-3 flex flex-col">
      {/* Sección 1: Plantilla, Título y descripción */}
      <div className="w-full pt-3 pb-6 px-4 border-b border-neutral-quiet flex flex-col gap-4">
        <TemplateSelector
          selectedTemplate={template}
          onTemplateChange={(value) => {
            setValue("template", value, { shouldValidate: true });
            if (value !== "no-template") {
              setValue("title", "");
              setValue("description", "");
            }
          }}
          error={errors.template?.message}
        />

        {isNoTemplate && (
          <>
            <TextField
              id="notification-title"
              label="Título de la notificación"
              required
              placeholder="Escribe el título de la notificación"
              count={title.length}
              error={errors.title?.message}
              {...register("title")}
            />

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <BasicRichTextEditor
                  value={field.value}
                  onChange={field.onChange}
                  label="Descripción de la notificación"
                  required
                  placeholder="Escribe una descripción para la notificación"
                  minHeight="120px"
                  error={errors.description?.message}
                />
              )}
            />
          </>
        )}

        <div className="flex flex-col gap-2">
          <label className="font-semibold text-body-loud text-sm leading-2xl">
            Documentos adjuntos
          </label>
          <AttachmentInput
            variables={allVariables}
            selectedAttachments={attachments}
            onChange={(newAttachments) =>
              setValue("attachments", newAttachments, { shouldValidate: true })
            }
          />
          {errors.attachments && (
            <div className="text-danger flex items-center gap-1.5">
              <span className="text-current text-xs leading-xl font-normal">
                {errors.attachments.message}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Sección 3: Variables de plantilla */}
      {!isNoTemplate && selectedTemplate && (
        <div className="w-full pt-3 pb-6 px-4 border-b border-neutral-quiet flex flex-col gap-4">
          <label className="font-semibold text-body-loud text-sm leading-2xl">
            Detalles esperados
          </label>

          {selectedTemplate.required_variables &&
            selectedTemplate.required_variables.length > 0 &&
            selectedTemplate.required_variables.map((varName) => {
              const varConfig = selectedTemplate.variables[varName];
              const varEntries = templateVariables[varName] || [
                {
                  type: "inp",
                  value: "",
                  var_type: varConfig?.type.toUpperCase() as
                    | "STRING"
                    | "DATE"
                    | "FLOAT",
                },
              ];

              return (
                <TemplateVariableInput
                  key={varName}
                  variableName={varName}
                  variableType={varConfig?.type.toLowerCase() || "string"}
                  required={true}
                  description={varConfig?.description}
                  variables={allVariables}
                  elements={varEntries}
                  onChange={(newEntries) => {
                    const newVariables = { ...templateVariables };
                    newVariables[varName] = newEntries;
                    setValue("variables", newVariables, {
                      shouldValidate: true,
                    });
                  }}
                />
              );
            })}

          {selectedTemplate.optional_variables &&
            selectedTemplate.optional_variables.length > 0 &&
            selectedTemplate.optional_variables.map((varName) => {
              const varConfig = selectedTemplate.variables[varName];
              const varEntries = templateVariables[varName] || [
                {
                  type: "inp",
                  value: "",
                  var_type: varConfig?.type.toUpperCase() as
                    | "STRING"
                    | "DATE"
                    | "FLOAT",
                },
              ];

              return (
                <TemplateVariableInput
                  key={varName}
                  variableName={varName}
                  variableType={varConfig?.type.toLowerCase() || "string"}
                  required={false}
                  description={varConfig?.description}
                  variables={allVariables}
                  elements={varEntries}
                  onChange={(newEntries) => {
                    const newVariables = { ...templateVariables };
                    newVariables[varName] = newEntries;
                    setValue("variables", newVariables, {
                      shouldValidate: true,
                    });
                  }}
                />
              );
            })}

          {errors.variables?.message &&
            typeof errors.variables.message === "string" && (
              <div className="text-danger flex items-center gap-1.5">
                <span className="text-current text-xs leading-xl font-normal">
                  {errors.variables.message}
                </span>
              </div>
            )}
        </div>
      )}

      {/* Sección 4: Correos de destino */}
      <div className="w-full pt-3 pb-6 px-4 border-b border-neutral-quiet flex flex-col gap-2">
        <label className="font-semibold text-body-loud text-sm leading-2xl">
          Correos de destino
        </label>
        <RecipientInput
          type="email"
          variables={allVariables}
          elements={emails}
          onChange={(newEmails) =>
            setValue("emails", newEmails, { shouldValidate: true })
          }
        />
        {errors.emails && (
          <div className="text-danger flex items-center gap-1.5">
            <span className="text-current text-xs leading-xl font-normal">
              {errors.emails.message}
            </span>
          </div>
        )}
      </div>

      {/* Sección 5: Teléfonos de destino - COMENTADO TEMPORALMENTE */}
      {/* <div className="w-full pt-3 pb-6 px-4 flex flex-col gap-2">
        <label className="font-semibold text-body-loud text-sm leading-2xl">
          Teléfonos de destino
        </label>
        <RecipientInput
          type="phone"
          variables={allVariables}
          elements={phones}
          onChange={(newPhones) => setValue("phones", newPhones, { shouldValidate: true })}
        />
        {errors.phones && (
          <div className="text-danger flex items-center gap-1.5">
            <span className="text-current text-xs leading-xl font-normal">
              {errors.phones.message}
            </span>
          </div>
        )}
      </div> */}
    </div>
  );
};
